/* --external-- */
import React, { useMemo, useState, useCallback, useEffect } from "react";
import _ from "lodash";
import classnames from "classnames";
import PropTypes from "prop-types";
import {
  DeleteOutlined,
  MicrophoneOutlined,
  PlaceholderFileUpArrowOutlined,
} from "@toddle-edu/ds-icons";
import { Button, IconButton } from "@toddle-edu/ds-web";
import { connect } from "react-redux";

/* --internal-- */
import FullScreenLoader from "UIComponents/FullScreenLoader/FullScreenLoader";
import AudioRecorderSelector from "UIComponents/AudioRecorderSelector";
import {
  MODE_STUDENT_ATTEMPT,
  MODE_STUDENT_VIEW,
  MODE_TEACHER_PREVIEW,
  DEFAULT_MEDIA_SUBMISSION_METHOD,
  ALLOWED_AUDIO_FILE_TYPES,
} from "Worksheet/modules/WorksheetConstants";
import { updateQuestionFieldsInCache } from "Worksheet/modules/WorksheetUtils";
import { updateIsQuestionTheaterModeEnable } from "Worksheet/modules/WorksheetModules";
import DropzoneComponent from "UIComponents/DropzoneComponent";
import { createAttachment } from "modules/Services";

/* --relative-- */
import classes from "./AudioResponseField.scss";

/* --constants-- */
const DIALOGS_INFO = {
  title: "discard_recording",
  message: "discard_recording_text",
  confirmButtonText: "discard_and_close_text",
  cancelButtonText: "keep",
  label: "",
};

const styles = {
  deleteButtonStyle: {
    marginInlineEnd: "-14px",
  },
  mediaPlaceholderContainerStyle: {
    borderColor: "var(--border-secondary)",
  },
};

// ButtonComponent renders a button with a microphone icon and dynamic text based on the recording state.
const ButtonComponent = ({
  t,
  isRecording,
  testId,
  isUploading,
  selectedMethod,
}) => {
  const isDisabled = isRecording || isUploading;

  return (
    <Button
      dsVersion="2.0"
      variant="neutral"
      type="outlined"
      size="large"
      icon={<MicrophoneOutlined />}
      testId={`${testId}-recorder`}
      disabled={isDisabled}
    >
      {isUploading && selectedMethod === "record"
        ? t("worksheet:uploading_audio")
        : isRecording
        ? t("worksheet:recording_in_progress")
        : t("common:add_with_label", {
            label: t("common:audio_response"),
          })}
    </Button>
  );
};

/**
 * AudioResponseField is a React component that renders an audio recording field
 * for users to record and upload audio responses. It supports different modes
 * (e.g., teacher preview, student attempt) and handles audio file attachments.
 */
const AudioResponseField = props => {
  const {
    value,
    t,
    parentType,
    fieldId,
    updateInputField,
    mode,
    fieldMode,
    audioDurationLimit,
    questionId,
    isSubQuestion,
    isFromInternalTool,
    subQuestionParentId,
    makeAutoSubmitOnTimesUp = false,
    updateIsQuestionTheaterModeEnable = _.noop,
    submissionMethod,
    portalType,
    createAttachment,
    isQueriesV2Enabled = false,
  } = props;
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [localAttachment, setLocalAttachment] = useState({});
  const [selectedMethod, setSelectedMethod] = useState();

  const isTeacherPreviewMode = mode === MODE_TEACHER_PREVIEW;

  useEffect(() => {
    if (mode === MODE_TEACHER_PREVIEW) return;
    if (isRecording) updateIsQuestionTheaterModeEnable(true);
  }, [isRecording]);

  useEffect(() => {
    return () => updateIsQuestionTheaterModeEnable(false);
  }, []);

  /**
   * Memoized attachment object based on the current mode.
   * In MODE_TEACHER_PREVIEW, uses `localAttachment`.
   * Otherwise, uses `value`.
   * If the selected object is empty, returns an empty object.
   * Ensures the returned object has a `url` property, preferring `signedUrl` if available, otherwise falls back to `url`.
   */
  const attachment = useMemo(() => {
    const obj = isTeacherPreviewMode ? localAttachment : value;
    if (_.isEmpty(obj)) return {};
    return {
      ...obj,
      url: _.get(obj, "signedUrl", obj?.url),
    };
  }, [value, localAttachment, isTeacherPreviewMode]);

  /**
   * Handles the audio file input for the audio response field.
   * Sets the local attachment and uploading state in teacher preview mode.
   * Otherwise, updates the question fields in cache, resets the input field,
   * and updates the uploading state.
   */
  const handleAudioFile = useCallback(
    params => {
      const attachment = _.get(params, "recording") ?? params ?? {};
      if (isTeacherPreviewMode) {
        setLocalAttachment(attachment);
        setIsUploading(false);
        return;
      }

      updateQuestionFieldsInCache({
        fields: [
          {
            id: fieldId,
            resolveFields: { id: fieldId, attachments: [attachment] },
            value: null,
          },
        ],
        questionId,
        isSubQuestion,
        isFromInternalTool,
        subQuestionParentId,
        isQueriesV2Enabled,
      });
      updateInputField({});
      setTimeout(
        () => {
          setIsUploading(false);
        },
        makeAutoSubmitOnTimesUp ? 1500 : 0
      );
      updateIsQuestionTheaterModeEnable(false);
    },
    [
      fieldId,
      isFromInternalTool,
      isSubQuestion,
      isTeacherPreviewMode,
      questionId,
      subQuestionParentId,
      updateInputField,
      makeAutoSubmitOnTimesUp,
    ]
  );

  /**
   * Handles the file upload event by updating the uploading state.
   *
   * @param {Object} param0 - The parameter object.
   * @param {Object} param0.attachment - The uploaded file attachment.
   */
  const handleFileUpload = ({ attachment }) => {
    setIsUploading(!_.isEmpty(attachment));
  };

  /**
   * Handles the creation of an audio file attachment for method type upload.
   *
   * If in teacher preview mode, sets the local attachment and skips uploading.
   * Otherwise, uploads the file as an attachment and processes the uploaded audio file.
   *
   * @function
   * @param {File} file - The audio file to be attached and uploaded.
   * @returns {Promise<void>} Resolves when the attachment process is complete.
   */
  const handleCreateAttachment = useCallback(
    async file => {
      if (isTeacherPreviewMode) {
        setLocalAttachment(file);
        setIsUploading(false);
        return;
      }
      setIsUploading(true);
      const attachments = await createAttachment({
        attachment: file,
        nodeId: fieldId,
        type: parentType,
        parentType,
        portalType,
      });
      handleAudioFile(_.first(attachments));
    },
    [
      isTeacherPreviewMode,
      fieldId,
      parentType,
      portalType,
      handleAudioFile,
      createAttachment,
    ]
  );

  const labelClasses = classnames("text-body-s", classes.label);

  const label =
    _.isEmpty(attachment) &&
    _.includes([MODE_TEACHER_PREVIEW, MODE_STUDENT_ATTEMPT], mode)
      ? t("worksheet:max_duration_in_min", {
          count: audioDurationLimit / 60000,
        })
      : _.includes(
          [MODE_TEACHER_PREVIEW, MODE_STUDENT_ATTEMPT, MODE_STUDENT_VIEW],
          mode
        )
      ? t("common:your_response")
      : t("worksheet:student_response");

  const testId = "worksheet-audio-questionType-response";

  // * Fullscreen loader when uploading audio file when times up
  if (makeAutoSubmitOnTimesUp && isUploading) return <FullScreenLoader />;

  // the audio recorder is used to display the audio file too(media card)
  const showAudioRecorder =
    _.includes(["record_or_upload", "record_only"], submissionMethod) ||
    !_.isEmpty(attachment);

  const showAudioUploader =
    _.includes(["record_or_upload", "upload_only"], submissionMethod) &&
    _.isEmpty(attachment);

  const audioContainerClasses = classnames(classes.audioContainer, {
    [classes.audioContainerWithAttachment]: !_.isEmpty(attachment),
  });

  const showLabel = !(
    _.isEmpty(attachment) &&
    !_.includes([MODE_TEACHER_PREVIEW, MODE_STUDENT_ATTEMPT], mode)
  );

  return (
    <div className={classes.container}>
      {showLabel && <div className={labelClasses}>{label}</div>}
      <div className={audioContainerClasses}>
        {showAudioRecorder && (
          <AudioRecorderSelector
            version="v5"
            showValue={!_.isEmpty(attachment)}
            value={attachment}
            showWaveform={true}
            buttonComponent={
              <ButtonComponent
                t={t}
                isRecording={isRecording}
                testId={testId}
                isUploading={isUploading}
                selectedMethod={selectedMethod}
              />
            }
            limit={makeAutoSubmitOnTimesUp ? 0 : audioDurationLimit}
            onFileUpload={handleFileUpload}
            updateInputField={handleAudioFile}
            parentId={fieldId}
            fieldId={fieldId}
            parentType={parentType}
            updateRecordingStatus={value => {
              setIsRecording(value);
              setSelectedMethod("record");
            }}
            dialogsInfo={DIALOGS_INFO}
            name={isTeacherPreviewMode ? "" : "recording"}
            mode={fieldMode}
            mediaPlaceholderContainerStyle={
              styles.mediaPlaceholderContainerStyle
            }
            showAudioDownloadButton={false}
            playAudioOnSeek={true}
            shouldBeResponsive={true}
            customDeleteButton={
              <IconButton
                dsVersion="2.0"
                icon={<DeleteOutlined />}
                style={styles.deleteButtonStyle}
                variant="neutral"
                type="plain"
                size="medium"
                testId={`${testId}-recording-delete`}
              />
            }
          />
        )}

        {showAudioUploader && (
          <DropzoneComponent
            accept={ALLOWED_AUDIO_FILE_TYPES}
            updateFilesCount={_.noop}
            showConfirmationDialogue={true}
            onSingleContentUploaded={handleCreateAttachment}
            updateIsUploading={value => {
              updateIsQuestionTheaterModeEnable(value);
              setIsUploading(value);
              setSelectedMethod("upload");
            }}
            renderComponent={
              <Button
                dsVersion="2.0"
                variant="neutral"
                type="outlined"
                size="large"
                icon={<PlaceholderFileUpArrowOutlined />}
                testId={`${testId}-recorder`}
                disabled={isRecording || isUploading}
              >
                {isUploading && selectedMethod === "upload"
                  ? t("worksheet:uploading_audio")
                  : t("worksheet:upload_file", {
                      type: t("common:audio"),
                    })}
              </Button>
            }
          />
        )}
      </div>
    </div>
  );
};

AudioResponseField.propTypes = {
  value: PropTypes.object,
  t: PropTypes.func,
  fieldId: PropTypes.string,
  parentType: PropTypes.string,
  updateInputField: PropTypes.func,
  mode: PropTypes.string,
  fieldMode: PropTypes.string,
  audioDurationLimit: PropTypes.number,
  questionId: PropTypes.string,
  isSubQuestion: PropTypes.bool,
  isFromInternalTool: PropTypes.bool,
  subQuestionParentId: PropTypes.string,
  submissionMethod: PropTypes.string,
  portalType: PropTypes.string,
  createAttachment: PropTypes.func,
  isQueriesV2Enabled: PropTypes.bool,
};

AudioResponseField.defaultProps = {
  value: {},
  parentType: "QUESTION_SUBMISSION_ATTACHMENT",
  t: key => key,
  updateInputField: _.noop,
  audioDurationLimit: 60000,
  isSubQuestion: false,
  isFromInternalTool: false,
  submissionMethod: DEFAULT_MEDIA_SUBMISSION_METHOD,
  createAttachment: _.noop,
  isQueriesV2Enabled: false,
};

const mapStateToProps = state => ({
  makeAutoSubmitOnTimesUp: _.get(state, "worksheet.enableAutoSubmit", false),
});

const mapActionCreators = {
  createAttachment,
  updateIsQuestionTheaterModeEnable,
};

export default connect(mapStateToProps, mapActionCreators)(AudioResponseField);
